import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { api } from "services/api.service";

const POLL_INTERVAL_MS = 1000;
const MAX_POLLS = 120;
const EXPORT_TIMEOUT_MS = MAX_POLLS * POLL_INTERVAL_MS;
const STATUS_POLL_RETRIES = 3;

type AssetExportStatus = {
  id: string;
  status: "processing" | "ready" | "failed";
};

export default function useAssetExport() {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [deadlineReached, setDeadlineReached] = useState(false);
  const [downloadDismissed, setDownloadDismissed] = useState(false);
  const [exportId, setExportId] = useState<string | null>(null);

  useEffect(() => {
    if (startedAt === null) {
      setDeadlineReached(false);
      return;
    }

    const remaining = EXPORT_TIMEOUT_MS - (Date.now() - startedAt);
    if (remaining <= 0) {
      setDeadlineReached(true);
      return;
    }

    setDeadlineReached(false);
    const timeoutId = window.setTimeout(() => setDeadlineReached(true), remaining);
    return () => window.clearTimeout(timeoutId);
  }, [startedAt]);

  const startExport = useMutation({
    mutationFn: (args: string) => api.post<AssetExportStatus>(`/assets_exports?${args}`).then(({ data }) => data),
  });

  const deadlineElapsed = deadlineReached || (startedAt !== null && Date.now() - startedAt >= EXPORT_TIMEOUT_MS);

  const statusQuery = useQuery({
    queryKey: ["assets_export", exportId],
    queryFn: () => api.get<AssetExportStatus>(`/assets_exports/${exportId}`).then(({ data }) => data),
    enabled: Boolean(exportId),
    retry: STATUS_POLL_RETRIES,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (deadlineElapsed) return false;
      if (!data || data.status !== "processing") return false;
      return POLL_INTERVAL_MS;
    },
  });

  const status = statusQuery.data?.status;
  const timedOut = deadlineElapsed && status !== "ready" && status !== "failed";
  const fileQuery = useQuery({
    queryKey: ["assets_export_file", exportId],
    queryFn: () => api.get<{ url: string }>(`/assets_exports/${exportId}/download`).then(({ data }) => data.url),
    enabled: status === "ready" && !timedOut,
    retry: STATUS_POLL_RETRIES,
    staleTime: Infinity,
  });

  const processing = status === "processing" && !timedOut;
  const waitingForStatus = Boolean(exportId) && status === undefined && !timedOut && !statusQuery.isError;
  const failed = status === "failed" || timedOut;

  const exportAssets = () => {
    if (startExport.isPending || processing || waitingForStatus || fileQuery.isLoading) return;

    if (exportId) {
      queryClient.removeQueries({ queryKey: ["assets_export", exportId] });
      queryClient.removeQueries({ queryKey: ["assets_export_file", exportId] });
    }

    setExportId(null);
    setDownloadDismissed(false);
    setStartedAt(Date.now());
    startExport.mutate(searchParams.toString(), {
      onSuccess: (exportJob) => setExportId(exportJob.id),
    });
  };

  const isExporting = !timedOut && (startExport.isPending || processing || waitingForStatus || fileQuery.isLoading);

  return {
    isError: startExport.isError || statusQuery.isError || fileQuery.isError || failed,
    isExporting,
    exportUrl: fileQuery.data ?? "",
    downloadAssetsExport: Boolean(fileQuery.data) && !downloadDismissed,
    setDownloadAssetsExport: (value: boolean) => {
      if (!value) setDownloadDismissed(true);
    },
    exportAssets,
  };
}
